const autoTagSpacesContent = {
  title: "Auto-Tag in Spaces",
  description: "Learn from how you've tagged files in a folder, then suggest and apply tags to untagged files in a Sortio Space.",
  sections: [
    {
      id: "overview",
      title: "What Auto-Tag Does",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          As of Sortio v8.11.0, every <a href="/docs/spaces" class="text-[#06c]">Space</a> has an
          <strong>Auto-tag</strong> panel. Auto-tag profiles the macOS Finder tags you've already applied
          inside a folder and uses that profile to suggest tags for any files that aren't tagged yet.
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          Instead of forcing you to predefine a taxonomy, Auto-tag <em>learns</em> from the work you've
          already done. If half your "2024 Taxes" folder is tagged Receipt, Invoice, or Statement, Sortio
          will suggest those same tags for the untagged files in the folder, with a per-file confidence
          score.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">1. Build</h4>
            <p class="text-[#424245] text-sm">Scan a folder you've already tagged and build a tag corpus.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">2. Suggest</h4>
            <p class="text-[#424245] text-sm">Get per-file tag suggestions with confidence scores.</p>
          </div>
          <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">3. Apply</h4>
            <p class="text-[#424245] text-sm">Approve the chips you like, then write the tags to disk.</p>
          </div>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">ℹ️</span>
            macOS only
          </h4>
          <p class="text-[#424245] text-sm">Finder tags are a macOS feature, so the Auto-tag panel only appears on Mac builds of Sortio. On Windows and Linux you'll see Spaces without this panel.</p>
        </div>
      `
    },
    {
      id: "workflow",
      title: "The Build / Suggest / Apply Workflow",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Open any Space, scroll to the <strong>Auto-tag</strong> panel, and walk through these three
          steps. The whole flow stays in the panel: no separate window, no setup wizard.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">Step-by-step</h4>
          </div>
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">1</span>
                </div>
                Pick a "Learn from" folder
              </h5>
              <p class="mt-2 text-[#424245] ml-10">By default this is the Space's own folder. You can point it at any other folder where you've tagged files. This is the source of the tag profile, not the destination.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">2</span>
                </div>
                Click "Build tag corpus"
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Sortio scans the source folder, reads each file's Finder tags, and profiles which tags appear on which kinds of files. The result is a tag corpus stored locally with the Space.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">3</span>
                </div>
                Click "Suggest tags for untagged files"
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Sortio runs the corpus against the Space and produces a per-file list of suggested tags. Each suggestion shows a confidence score and is rendered as an approvable chip.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">4</span>
                </div>
                Approve or unapprove chips
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Click a chip to toggle its approved state. You stay in control of every tag that ends up on every file. Sortio never writes tags to disk during the suggestion step.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">5</span>
                </div>
                Click "Apply selected tags"
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Sortio writes the approved tags to the files using the macOS Finder tag API. From here on you can act on those tags from <a href="/docs/finder-tags" class="text-[#06c]">Finder-tag-aware Automations</a>, the macOS sidebar, Spotlight, or any tool that reads Finder tags.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "ai-vs-heuristic",
      title: "AI Mode vs. Deterministic Heuristic",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          The Auto-tag panel has a <strong>Use AI</strong> toggle, on by default. It controls how Sortio
          decides which tags to suggest for each file.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Use AI: on (default)</h4>
            <p class="text-[#424245] text-sm mb-3">Sortio sends each candidate file's profile and the tag corpus to an AI model and asks for the most likely tags. Quality scales with the model you've selected.</p>
            <p class="text-[#86868b] text-xs">Each AI suggestion call uses <strong>a small part of your AI allowance</strong>.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Use AI: off</h4>
            <p class="text-[#424245] text-sm mb-3">Sortio uses a deterministic heuristic over filenames, paths, and the corpus. It's free, fast, and entirely local.</p>
            <p class="text-[#86868b] text-xs">Doesn't use your AI allowance at all. Works offline.</p>
          </div>
        </div>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-4">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">"Only untagged files" toggle</h4>
              <p class="text-[#424245]">On by default. With this on, Sortio skips files that already have any Finder tag, so it never overwrites your existing work. Turn it off if you want suggestions for every file in the Space, including ones that already have tags.</p>
            </div>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          A reasonable starting pattern: leave both toggles at their defaults (AI on, untagged only). If
          you're working through a very large folder and want to keep AI usage low, run the heuristic first
          to handle the obvious cases, then run AI on whatever remains.
        </p>
      `
    },
    {
      id: "tagfiler-hazel",
      title: "Works with TagFiler / Hazel-Style Workflows",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Auto-tag was designed to slot in next to existing tag-driven workflows on macOS. If you already
          use Brett Terpstra's <strong>TagFiler</strong> or rules in <strong>Hazel</strong> to route files
          based on Finder tags, Auto-tag handles the part those tools don't: <em>getting tags onto files in
          the first place</em>.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">A typical chain</h4>
          </div>
          <div class="p-6 space-y-3 text-[#424245]">
            <p>1. You drop a batch of new documents into a Space's folder.</p>
            <p>2. Sortio's Auto-tag panel suggests tags for the untagged files based on what you've already tagged.</p>
            <p>3. You approve the chips that look right and click <strong>Apply selected tags</strong>.</p>
            <p>4. TagFiler, Hazel, or Sortio's own <a href="/docs/finder-tags" class="text-[#06c]">Finder-tag Automations</a> pick up the newly tagged files and route them.</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          The corpus model is intentional here: rather than asking you to maintain a tag taxonomy, Sortio
          extracts one from the folder you point it at. That keeps the friction of bringing new files into
          an established workflow close to zero.
        </p>
      `
    },
    {
      id: "tips",
      title: "Tips & Caveats",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Start with a folder that's well-tagged</h4>
              <p class="text-[#424245]">The corpus is only as good as the folder you learn from. If you're starting fresh, tag 20-50 representative files by hand first, then build the corpus from that folder.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Confidence scores aren't accuracy guarantees</h4>
              <p class="text-[#424245]">Treat the per-chip confidence as a sort order, not a verdict. When in doubt, click into the file in Finder and verify before approving the tag.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Suggestions are non-destructive</h4>
              <p class="text-[#424245]">Nothing is written to disk until you click <strong>Apply selected tags</strong>. You can rebuild the corpus and re-run suggestions as many times as you want without affecting any files.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Combine with Finder-tag Automations</h4>
              <p class="text-[#424245]">Once tags are on the files, build deterministic rules in <a href="/docs/finder-tags" class="text-[#06c]">macOS Finder Tag Automations</a> to route, color, or rename based on those tags. Auto-tag and Automations were designed to work together.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">AI usage</h4>
              <p class="text-[#424245]">When <strong>Use AI</strong> is on, each AI suggestion call uses a small part of your AI allowance. The deterministic heuristic is free and doesn't use your AI allowance at all. Both modes only run when you click Suggest, so you never spend allowance unintentionally.</p>
            </div>
          </div>
        </div>
      `
    }
  ]
};

export default autoTagSpacesContent;
